import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DtoCubeDataResponse } from "../../../../../../types/dto-types";
import { CATEGORY_ICONS } from "../../../components/ChallengeRow";
import { Cube } from "../../../components/Сube";
import { CUBE_SIDE_NUMBER_TO_NAME } from "../../../components/Сube/constants";
import { CubeSideName } from "../../../components/Сube/types";
import { SelectedFilters, useUrlState } from "../../../hooks";
import { useSolutionHomePageData } from "../../../hooks/useSolutionHomePageData";
import { CategoryNavItem } from "../../../types";
import { calculatePercentagesForNumbers, renderNumber } from "../../../utils";
import "./style.scss";
import { SolutionJourney } from "../../../components/SolutionJourney";

export function HomePage({
  rawData,
  currentTestId,
  selectedFilters,
}: {
  rawData?: DtoCubeDataResponse;
  currentTestId: string;
  selectedFilters: SelectedFilters | undefined;
}) {
  const { t } = useTranslation();
  const { updatePath } = useUrlState();

  const [hoveredCubeSide, setHoveredCubeSide] = useState<CubeSideName>();

  function navigateToCategory(category: CategoryNavItem) {
    updatePath({ category });
  }

  const { cubeValuesByCategory, totalJourneys, solutionJourney } =
    useSolutionHomePageData({
      rawData,
      currentTestId,
      selectedFilters,
    });

  const sidesSaturation = calculatePercentagesForNumbers({
    "2": cubeValuesByCategory.organization,
    "3": cubeValuesByCategory.leadership,
    "4": cubeValuesByCategory.team,
  });

  const renderCubeInfo = (category: CategoryNavItem) => {
    const value = cubeValuesByCategory?.[category] ?? 0;
    const title = t(
      `frontend.futurebilityCockpit.categories.${category}.title`,
      category
    );
    const isActive =
      hoveredCubeSide && CUBE_SIDE_NUMBER_TO_NAME[hoveredCubeSide] === category;

    return (
      <div
        className={`cube-info ${isActive ? "active" : ""}`}
        onClick={() => navigateToCategory(category)}
        style={{ cursor: "pointer" }}
      >
        <div className="cube-inner">
          <div className="cube-title">
            <div className="cube-icon">{(CATEGORY_ICONS as any)[category]}</div>
            <div>{title}</div>
          </div>
          <div className="cube-value">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="26"
              viewBox="0 0 23 26"
              fill="none"
            >
              <path
                d="M21.6954 24.249L21.6954 21.6934C21.6954 20.3377 21.1569 19.0377 20.1984 18.0791C19.2398 17.1205 17.9397 16.582 16.5841 16.582L6.36136 16.582C5.00574 16.582 3.70565 17.1205 2.74708 18.0791C1.78852 19.0377 1.25 20.3377 1.25 21.6934L1.25 24.249"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.4702 11.4726C14.2932 11.4726 16.5816 9.18423 16.5816 6.36132C16.5816 3.53842 14.2932 1.25 11.4702 1.25C8.64732 1.25 6.35889 3.53842 6.35889 6.36132C6.35889 9.18423 8.64732 11.4726 11.4702 11.4726Z"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {renderNumber(value, false)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="solution-home-page">
      <div className="left">
        <section className="cube-overview">
          <section className="cube-section">
            <div className="cube-wrap">
              <div className="cube-top">{renderCubeInfo("organization")}</div>
              <div className="cube-center">
                <Cube
                  greyOutOtherSidesOnHover
                  values={{ 1: 0, 2: 100, 3: 100, 4: 100, 5: 0, 6: 0 }}
                  sidesSaturation={sidesSaturation}
                  mode="collapsed"
                  onSideHover={side => {
                    setHoveredCubeSide(side);
                  }}
                  onSideClick={side => {
                    updatePath({
                      category: CUBE_SIDE_NUMBER_TO_NAME[
                        side
                      ] as CategoryNavItem,
                    });
                  }}
                />
              </div>
              <div className="cube-bottom">
                {renderCubeInfo("team")}
                {renderCubeInfo("leadership")}
              </div>
            </div>
          </section>
        </section>
      </div>
      <div className="right">
        <div className="right-title">
          {t(
            "frontend.futurebilityCockpit.solution.journey.title",
            "Solution Journey"
          )}
        </div>
        <SolutionJourney
          journey1={solutionJourney[0]}
          journey2={solutionJourney[1]}
          journey3={solutionJourney[2]}
        />
        <div className="overview">
          <div className="title">
            {t(
              "frontend.futurebilityCockpit.solution.overview.title",
              "Totally {{count}} Journeys available",
              { count: totalJourneys }
            )}
          </div>
          <div className="text">
            {t(
              "frontend.futurebilityCockpit.solution.overview.description",
              "Pick a category and then topic to see the summaries of solutions of each Solution Journey"
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
